import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Pencil, Trash2, Send, Settings2, UserPlus, Image as ImageIcon, Square, CheckSquare, UserX, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import WhatsAppMigrationWidget from "@/components/WhatsAppMigrationWidget";

const MIG_STATUS_LABEL = { creada: "Creada", esperando_qr: "Esperando QR", conectado: "Conectado", cerrada: "Cerrada", error: "Error" };
// El WhatsApp conectado es el celular personal de Seba — solo él puede ver el contenido de
// charlas ocultas/personales (el backend también lo exige en GET /whatsapp/chats).
const WHATSAPP_OWNER_EMAIL = "gr.soluciones.integrales@gmail.com";

export default function WhatsApp() {
  const location = useLocation();
  const prefill = location.state || {};
  const isWaOwner = JSON.parse(localStorage.getItem("gr_user") || "{}").email === WHATSAPP_OWNER_EMAIL;
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("charlas");
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ api_url: "", api_key: "", instance: "" });
  const [tplEdit, setTplEdit] = useState(null);
  const [tplId, setTplId] = useState(null);
  const [waStatus, setWaStatus] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [qr, setQr] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const pollRef = useRef(null);
  const [send, setSend] = useState({ phone: prefill.phone || "", template_id: "", message: "", variables: { nombre: prefill.nombre || "", numero: prefill.numero || "", total: "" } });
  const [sending, setSending] = useState(false);

  // ---- Charlas ----
  const [chats, setChats] = useState([]);
  const [activePhone, setActivePhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [chatSearch, setChatSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [selectingPersonal, setSelectingPersonal] = useState(false);
  const [selectedPersonal, setSelectedPersonal] = useState(new Set());
  const [excludingPersonal, setExcludingPersonal] = useState(false);
  const showHiddenRef = useRef(false);
  const [linkChat, setLinkChat] = useState(null); // phone del chat a vincular, o null
  const [linkName, setLinkName] = useState("");
  const [linkClientId, setLinkClientId] = useState("");
  const [fullImage, setFullImage] = useState(null);
  const msgEndRef = useRef(null);
  const chatsPollRef = useRef(null);
  const msgsPollRef = useRef(null);

  // ---- Migración (Fase A) ----
  const [migrationInstances, setMigrationInstances] = useState([]);
  const migrationPollRef = useRef(null);
  const loadMigrationInstances = () =>
    api.get("/whatsapp/migration/instances").then((r) => setMigrationInstances(r.data)).catch(() => {});

  const deleteMigrationInstance = async (inst) => {
    if (!window.confirm(`¿Borrar el registro de "${inst.label}"? No borra las charlas/mensajes ya aportados (eso se hace desde Charlas), solo la fila de esta instancia.`)) return;
    try {
      await api.delete(`/whatsapp/migration/instances/${inst.id}`);
      toast.success("Instancia eliminada");
      loadMigrationInstances();
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    if (tab !== "migracion") { clearInterval(migrationPollRef.current); return; }
    loadMigrationInstances();
    migrationPollRef.current = setInterval(loadMigrationInstances, 5000);
    return () => clearInterval(migrationPollRef.current);
  }, [tab]); // eslint-disable-line

  const loadChats = () =>
    api.get("/whatsapp/chats", { params: showHiddenRef.current ? { include_hidden: true } : {} })
      .then((r) => setChats(r.data)).catch(() => {});

  const toggleHidden = (value) => {
    showHiddenRef.current = value;
    setShowHidden(value);
    loadChats();
  };

  const setChatHidden = async (phone, hidden) => {
    try {
      await api.post(`/whatsapp/chats/${phone}/hide`, { hidden });
      toast.success(hidden ? "Charla oculta — deja de trackearse" : "Charla restaurada — vuelve a trackearse");
      if (hidden && activePhone === phone) setActivePhone(null);
      loadChats();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteChatHistory = async (phone) => {
    if (!window.confirm("¿Borrar el historial completo de esta charla? Es irreversible (no borra al cliente/proveedor vinculado, si lo tiene).")) return;
    try {
      await api.delete(`/whatsapp/chats/${phone}`);
      toast.success("Historial de la charla eliminado");
      if (activePhone === phone) setActivePhone(null);
      loadChats();
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePersonalSelection = (phone) => {
    setSelectedPersonal((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  };

  const cancelPersonalSelection = () => {
    setSelectingPersonal(false);
    setSelectedPersonal(new Set());
  };

  const excludePersonalChats = async (phones = [...selectedPersonal]) => {
    if (!phones.length) return;
    const label = phones.length === 1 ? "esta charla" : `estas ${phones.length} charlas`;
    if (!window.confirm(`¿Marcar ${label} como personal y quitarla de GR?\n\nSe borra su historial del programa y los mensajes nuevos dejan de seguirse. No se borra nada de WhatsApp.`)) return;
    setExcludingPersonal(true);
    try {
      await api.post("/whatsapp/chats/exclude-personal", { phones });
      toast.success(phones.length === 1 ? "Contacto personal quitado de GR" : `${phones.length} contactos personales quitados de GR`);
      if (phones.includes(activePhone)) setActivePhone(null);
      cancelPersonalSelection();
      loadChats();
    } catch (e) { toast.error(apiError(e)); }
    finally { setExcludingPersonal(false); }
  };
  const loadMessages = (phone) => api.get(`/whatsapp/chats/${phone}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  const loadChannels = (phone) =>
    api.get(`/whatsapp/chats/${phone}/channels`).then((r) => {
      setChannels(r.data);
      // Por default responde por el mismo canal del último mensaje recibido, si sigue
      // conectado; si no, cae a la Empresa (siempre disponible y siempre conectada).
      setSelectedChannel((prev) => {
        if (prev && r.data.some((c) => c.instance === prev && c.connected)) return prev;
        const company = r.data.find((c) => c.is_company);
        return company ? company.instance : r.data[0]?.instance || null;
      });
    }).catch(() => {});

  useEffect(() => {
    if (tab !== "charlas") { clearInterval(chatsPollRef.current); return; }
    loadChats();
    chatsPollRef.current = setInterval(loadChats, 8000);
    return () => clearInterval(chatsPollRef.current);
  }, [tab]); // eslint-disable-line

  useEffect(() => {
    clearInterval(msgsPollRef.current);
    if (!activePhone || tab !== "charlas") return;
    loadMessages(activePhone);
    msgsPollRef.current = setInterval(() => loadMessages(activePhone), 4000);
    return () => clearInterval(msgsPollRef.current);
  }, [activePhone, tab]); // eslint-disable-line

  useEffect(() => { msgEndRef.current?.scrollIntoView({ block: "nearest" }); }, [messages]);

  const openChat = (phone) => {
    setActivePhone(phone);
    setMessages([]);
    setChannels([]);
    setSelectedChannel(null);
    loadChannels(phone);
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !activePhone) return;
    setSendingReply(true);
    setReply("");
    const tempId = `pending-${Date.now()}`;
    setMessages((prev) => [...prev, { msg_id: tempId, from_me: true, type: "text", text,
      thumbnail: "", instance: selectedChannel, created_at: new Date().toISOString(), pending: true }]);
    try {
      await api.post(`/whatsapp/chats/${activePhone}/reply`, { message: text, instance: selectedChannel });
      loadMessages(activePhone);
      loadChats();
      loadChannels(activePhone);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((prev) => prev.filter((m) => m.msg_id !== tempId));
      setReply(text);
    } finally { setSendingReply(false); }
  };

  const openLinkDialog = (chat) => {
    setLinkChat(chat.phone);
    setLinkName(chat.push_name || "");
    setLinkClientId("");
  };

  const confirmLink = async () => {
    try {
      const body = linkClientId ? { client_id: linkClientId } : { name: linkName };
      if (!linkClientId && !linkName.trim()) { toast.error("Elegí un cliente o ingresá un nombre"); return; }
      const { data } = await api.post(`/whatsapp/chats/${linkChat}/link-client`, body);
      toast.success(`Charla vinculada a ${data.client_name}`);
      setLinkChat(null);
      loadChats();
      if (!linkClientId) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const openFullImage = async (msgId, instance) => {
    setFullImage({ loading: true });
    try {
      const { data } = await api.get(`/whatsapp/media/${msgId}`, { params: instance ? { instance } : {} });
      setFullImage({ loading: false, src: `data:${data.mimetype};base64,${data.base64}` });
    } catch (e) { toast.error(apiError(e)); setFullImage(null); }
  };

  const fmtHora = (iso) => new Date(iso).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  const activeChat = chats.find((c) => c.phone === activePhone);
  const visibleChats = chats.filter((c) => {
    const q = chatSearch.trim().toLowerCase();
    if (!q) return true;
    return (c.client_name || "").toLowerCase().includes(q)
      || (c.push_name || "").toLowerCase().includes(q)
      || (c.phone || "").includes(q)
      || (c.last_text || "").toLowerCase().includes(q);
  });
  const hiddenTotal = chats[0]?.hidden_total || 0;
  const SUGGESTION_STYLE = {
    cliente: "text-green-400 border-green-500/40 bg-green-500/10",
    proveedor: "text-sky-400 border-sky-500/40 bg-sky-500/10",
    arquitecto: "text-violet-400 border-violet-500/40 bg-violet-500/10",
    personal: "text-neutral-400 border-neutral-600 bg-neutral-800",
  };
  const visibleMessages = messages.filter((m) => m.text || m.thumbnail || m.type === "image");

  const loadStatus = () => api.get("/whatsapp/status").then((r) => setWaStatus(r.data.state)).catch(() => {});

  const loadAll = () => {
    api.get("/whatsapp/settings").then((r) => { setSettings(r.data); setSettingsForm({ api_url: r.data.api_url, api_key: "", instance: r.data.instance }); }).catch(() => {});
    api.get("/whatsapp/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/whatsapp/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    loadStatus();
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const fetchQr = async () => {
    setLoadingQr(true);
    try {
      const { data } = await api.get("/whatsapp/qrcode");
      setQr(data.base64);
    } catch (e) { toast.error(apiError(e)); setShowQr(false); } finally { setLoadingQr(false); }
  };

  const openQr = () => { setQr(null); setShowQr(true); fetchQr(); };

  useEffect(() => {
    if (!showQr) { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      const { data } = await api.get("/whatsapp/status").catch(() => ({ data: {} }));
      setWaStatus(data.state);
      if (data.state === "open") {
        clearInterval(pollRef.current);
        setShowQr(false);
        toast.success("¡WhatsApp vinculado correctamente!");
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [showQr]);

  useEffect(() => {
    if (prefill.clientId && clients.length && !send.phone) {
      const c = clients.find((x) => x.id === prefill.clientId);
      if (c) setSend((s) => ({ ...s, phone: c.phone }));
    }
  }, [clients]); // eslint-disable-line

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/whatsapp/settings", settingsForm);
      setSettings(data);
      setShowSettings(false);
      toast.success(data.configured ? "Evolution API configurada" : "Guardado (modo simulado hasta completar los 3 campos)");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTemplate = async () => {
    if (!tplEdit.name || !tplEdit.body) { toast.error("Completá nombre y mensaje"); return; }
    try {
      if (tplId) await api.put(`/whatsapp/templates/${tplId}`, tplEdit);
      else await api.post("/whatsapp/templates", tplEdit);
      setTplEdit(null); setTplId(null);
      loadAll();
      toast.success("Plantilla guardada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/whatsapp/templates/${id}`); loadAll(); } catch (e) { toast.error(apiError(e)); }
  };

  const preview = () => {
    if (!send.template_id) return send.message;
    const tpl = templates.find((t) => t.id === send.template_id);
    if (!tpl) return "";
    let msg = tpl.body;
    Object.entries(send.variables).forEach(([k, v]) => { if (v) msg = msg.replaceAll(`{{${k}}}`, v); });
    return msg;
  };

  const doSend = async () => {
    if (!send.phone) { toast.error("Ingresá el número de teléfono"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/send", {
        phone: send.phone,
        template_id: send.template_id || null,
        message: send.template_id ? null : send.message,
        variables: send.variables,
      });
      toast.success(data.mock ? "Mensaje registrado en modo simulado (configurá Evolution API para envíos reales)" : "Mensaje enviado");
      loadAll();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  return (
    <div data-testid="whatsapp-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading font-bold uppercase text-2xl tracking-tight">WhatsApp</h1>
        <Button variant="outline" onClick={() => setShowSettings(true)} className="border-neutral-700 rounded-sm text-xs font-heading uppercase hover:bg-neutral-800 hover:text-white" data-testid="wa-settings-btn">
          <Settings2 className="w-4 h-4 mr-1" /> Configurar Evolution API
        </Button>
      </div>

      {settings?.mock_mode && (
        <div className="bg-[#F2B90D]/10 border border-[#F2B90D]/40 rounded-md px-4 py-3 mb-6 text-sm text-[#F2B90D]" data-testid="wa-mock-banner">
          Modo simulado: Evolution API no está configurada. Los mensajes se registran pero no se envían. Configurala con el botón de arriba cuando tengas tu instancia.
        </div>
      )}
      {settings && !settings.mock_mode && waStatus && waStatus !== "open" && (
        <div className="bg-[#F2B90D]/10 border border-[#F2B90D]/40 rounded-md px-4 py-3 mb-6 text-sm text-[#F2B90D] flex items-center justify-between gap-4 flex-wrap" data-testid="wa-unlinked-banner">
          <span>Evolution API configurada, pero el WhatsApp todavía no está vinculado a ningún celular (estado: {waStatus}).</span>
          <Button size="sm" onClick={openQr} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs shrink-0" data-testid="wa-link-btn">
            Vincular WhatsApp
          </Button>
        </div>
      )}
      {settings && !settings.mock_mode && waStatus === "open" && (
        <div className="bg-green-500/10 border border-green-500/40 rounded-md px-4 py-3 mb-6 text-sm text-green-400" data-testid="wa-linked-banner">
          WhatsApp vinculado y funcionando.
        </div>
      )}

      <div className="flex gap-2 mb-4 md:mb-6 overflow-x-auto pb-1 scroll-visible -mx-3 px-3 sm:mx-0 sm:px-0">
        {[["charlas", "Charlas"], ["enviar", "Enviar mensaje"], ["plantillas", "Plantillas"], ["historial", "Historial"], ["migracion", "Migración"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase font-bold rounded-sm border transition-colors shrink-0 ${tab === k ? "bg-[#F2B90D] border-[#F2B90D] text-black" : "border-neutral-700 text-neutral-400 hover:bg-neutral-800"}`}
            data-testid={`wa-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "charlas" && (
        <div className="grid md:grid-cols-[320px_1fr] gap-4 h-auto md:h-[calc(100vh-260px)] min-h-[420px]">
          <div className={`bg-[#171717] border border-neutral-800 rounded-md overflow-y-auto flex-col max-h-[calc(100vh-190px)] md:max-h-none ${activePhone ? "hidden md:flex" : "flex"}`} data-testid="wa-chats-list">
            {chats.length > 0 && (
              <div className="p-2 border-b border-neutral-800 sticky top-0 bg-[#171717] z-10 space-y-1.5">
                <Input value={chatSearch} onChange={(e) => setChatSearch(e.target.value)}
                  placeholder="Buscar charla..." className="bg-neutral-900 border-neutral-800 rounded-sm h-8 text-sm"
                  data-testid="wa-chat-search" />
                {isWaOwner && (hiddenTotal > 0 || showHidden) && (
                  <button onClick={() => toggleHidden(!showHidden)}
                    className="text-[11px] text-neutral-500 hover:text-neutral-300 transition-colors"
                    data-testid="wa-toggle-hidden">
                    {showHidden ? "Ocultar las charlas personales" : `Ver ocultas (${hiddenTotal})`}
                  </button>
                )}
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => selectingPersonal ? cancelPersonalSelection() : setSelectingPersonal(true)}
                    className={`text-[11px] transition-colors ${selectingPersonal ? "text-white" : "text-neutral-400 hover:text-white"}`}
                    data-testid="wa-select-personal">
                    {selectingPersonal ? "Cancelar selección" : "Seleccionar contactos personales"}
                  </button>
                  {selectingPersonal && selectedPersonal.size > 0 && (
                    <button onClick={() => excludePersonalChats()} disabled={excludingPersonal}
                      className="text-[11px] font-bold text-red-400 hover:text-red-300 disabled:opacity-50"
                      data-testid="wa-exclude-selected">
                      Quitar {selectedPersonal.size} de GR
                    </button>
                  )}
                </div>
              </div>
            )}
            {chats.length === 0 && (
              <p className="text-neutral-500 text-sm p-5">Todavía no llegó ningún mensaje. Cuando el WhatsApp de la empresa esté vinculado y alguien escriba, va a aparecer acá.</p>
            )}
            {chats.length > 0 && visibleChats.length === 0 && (
              <p className="text-neutral-500 text-sm p-5">Ninguna charla coincide con "{chatSearch}".</p>
            )}
            {visibleChats.map((c) => (
              <button key={c.phone} onClick={() => selectingPersonal ? togglePersonalSelection(c.phone) : openChat(c.phone)}
                className={`w-full text-left px-4 py-3 border-b border-neutral-800 last:border-0 hover:bg-neutral-800/60 transition-colors ${activePhone === c.phone ? "bg-neutral-800" : ""}`}
                data-testid={`wa-chat-item-${c.phone}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-heading font-bold text-sm truncate flex items-center gap-2">
                    {selectingPersonal && (selectedPersonal.has(c.phone)
                      ? <CheckSquare className="w-4 h-4 text-red-400 shrink-0" />
                      : <Square className="w-4 h-4 text-neutral-500 shrink-0" />)}
                    {c.client_name || c.push_name || c.phone}
                  </span>
                  <span className="text-[10px] text-neutral-500 shrink-0">{c.last_ts && fmtHora(c.last_ts)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-xs text-neutral-400 truncate">{c.last_from_me ? "Vos: " : ""}{c.last_text}</p>
                  {c.hidden
                    ? <span className="text-[9px] uppercase font-bold text-neutral-500 border border-neutral-700 bg-neutral-800/60 rounded-sm px-1.5 py-0.5 shrink-0">Oculta</span>
                    : c.client_id
                    ? <span className="text-[9px] uppercase font-bold text-green-400 border border-green-500/40 bg-green-500/10 rounded-sm px-1.5 py-0.5 shrink-0">Cliente</span>
                    : c.ai_suggestion
                    ? <span title={c.ai_reason || ""} className={`text-[9px] uppercase font-bold rounded-sm px-1.5 py-0.5 shrink-0 border ${SUGGESTION_STYLE[c.ai_suggestion] || SUGGESTION_STYLE.personal}`}>¿{c.ai_suggestion}?</span>
                    : <span className="text-[9px] uppercase font-bold text-[#F2B90D] border border-[#F2B90D]/40 bg-[#F2B90D]/10 rounded-sm px-1.5 py-0.5 shrink-0">Sin vincular</span>}
                </div>
              </button>
            ))}
          </div>

          <div className={`bg-[#171717] border border-neutral-800 rounded-md flex-col overflow-hidden h-[calc(100vh-190px)] md:h-auto ${activePhone ? "flex" : "hidden md:flex"}`}>
            {!activePhone && (
              <div className="flex-1 flex items-center justify-center text-neutral-500 text-sm">Elegí una charla de la izquierda</div>
            )}
            {activePhone && (
              <>
                <div className="px-3 md:px-5 py-3 border-b border-neutral-800 flex flex-wrap md:flex-nowrap items-center justify-between gap-2 md:gap-3">
                  <button onClick={() => setActivePhone(null)} className="md:hidden p-2 -ml-2 rounded-md hover:bg-neutral-800 shrink-0" aria-label="Volver a charlas">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-sm truncate">{activeChat?.client_name || activeChat?.push_name || activePhone}</p>
                    <p className="text-xs text-neutral-500 font-mono">{activePhone}</p>
                    {activeChat?.ai_reason && !activeChat?.client_id && (
                      <p className="text-[11px] text-neutral-500 truncate mt-0.5">Sugerencia: {activeChat.ai_reason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 overflow-x-auto w-full md:w-auto order-3 md:order-none pt-1 md:pt-0">
                    {!activeChat?.client_id && (
                      <Button size="sm" variant="outline" onClick={() => openLinkDialog(activeChat || { phone: activePhone })}
                        className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="wa-link-client-btn">
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Vincular cliente
                      </Button>
                    )}
                    {!activeChat?.hidden && (
                      <Button size="sm" variant="outline" onClick={() => excludePersonalChats([activePhone])}
                        className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="wa-exclude-personal-btn"
                        title="Quita la charla de GR y deja de seguir sus mensajes; no modifica WhatsApp">
                        <UserX className="w-3.5 h-3.5 mr-1" /> Personal / quitar
                      </Button>
                    )}
                    {activeChat?.hidden && (
                      <Button size="sm" variant="outline" onClick={() => setChatHidden(activePhone, false)}
                        className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="wa-hide-chat-btn">
                        Restaurar
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => deleteChatHistory(activePhone)}
                      className="border-red-900 rounded-sm text-xs text-red-400 hover:bg-red-500/10 hover:text-red-400" data-testid="wa-delete-chat-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#0b141a]">
                  {visibleMessages.map((m) => {
                    const ch = channels.find((c) => c.instance === m.instance);
                    const chLabel = ch ? ch.label : m.instance;
                    return (
                      <div key={m.msg_id} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-lg p-2.5 text-sm whitespace-pre-wrap ${m.from_me ? "bg-[#005c4b] text-white rounded-tr-none" : "bg-[#202c33] text-white rounded-tl-none"} ${m.pending ? "opacity-50" : ""}`}>
                          {channels.length > 1 && chLabel && (
                            <p className="text-[9px] uppercase font-bold text-white/50 mb-0.5">{chLabel}</p>
                          )}
                          {m.type === "image" && (
                            <button onClick={() => openFullImage(m.msg_id, m.instance)} className="block mb-1" data-testid={`wa-img-${m.msg_id}`}>
                              {m.thumbnail
                                ? <img src={`data:image/jpeg;base64,${m.thumbnail}`} alt="" className="rounded-md max-w-full max-h-56 object-cover" />
                                : <span className="flex items-center gap-1 text-white/70 text-xs"><ImageIcon className="w-4 h-4" /> Ver imagen</span>}
                            </button>
                          )}
                          {m.text && <p>{m.text}</p>}
                          <p className="text-[10px] text-white/50 text-right mt-1">{m.pending ? "Enviando..." : fmtHora(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={msgEndRef} />
                </div>
                <div className="p-3 border-t border-neutral-800 space-y-2">
                  {channels.length > 1 && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-neutral-500 shrink-0">Responder por:</span>
                      <select value={selectedChannel || ""} onChange={(e) => setSelectedChannel(e.target.value)}
                        className="bg-neutral-900 border border-neutral-800 rounded-sm px-2 py-1 text-xs flex-1"
                        data-testid="wa-reply-channel">
                        {channels.map((c) => (
                          <option key={c.instance} value={c.instance} disabled={!c.connected}>
                            {c.label}{!c.connected ? " (desconectado)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input value={reply} onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Escribí una respuesta..." className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-reply-input" />
                    <Button onClick={sendReply} disabled={sendingReply || !reply.trim()} className="bg-[#25D366] hover:bg-[#1faa52] text-white rounded-sm shrink-0" data-testid="wa-reply-send">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "enviar" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-[#171717] border border-neutral-800 rounded-md p-6 space-y-4">
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Contacto / cliente</label>
              <select onChange={(e) => { const c = clients.find((x) => x.id === e.target.value); if (c) setSend({ ...send, phone: c.phone, variables: { ...send.variables, nombre: c.name } }); }}
                defaultValue="" className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm mb-2" data-testid="wa-client-select">
                <option value="">— Elegir de mis clientes —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>)}
              </select>
              <Input placeholder="Número con código de país, ej: 5492901234567" value={send.phone} onChange={(e) => setSend({ ...send, phone: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-phone-input" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Plantilla (opcional)</label>
              <select value={send.template_id} onChange={(e) => setSend({ ...send, template_id: e.target.value })} className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm" data-testid="wa-template-select">
                <option value="">— Mensaje libre —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            {send.template_id ? (
              <div className="grid grid-cols-3 gap-2">
                {["nombre", "numero", "total"].map((v) => (
                  <div key={v}>
                    <label className="text-[10px] uppercase font-bold text-neutral-500 block mb-1">{`{{${v}}}`}</label>
                    <Input value={send.variables[v]} onChange={(e) => setSend({ ...send, variables: { ...send.variables, [v]: e.target.value } })} className="bg-neutral-900 border-neutral-800 rounded-sm h-9" data-testid={`wa-var-${v}`} />
                  </div>
                ))}
              </div>
            ) : (
              <Textarea placeholder="Escribí tu mensaje..." rows={4} value={send.message} onChange={(e) => setSend({ ...send, message: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-message-input" />
            )}
            <Button onClick={doSend} disabled={sending} className="w-full bg-[#25D366] hover:bg-[#1faa52] text-white rounded-sm font-heading uppercase" data-testid="wa-send-btn">
              <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar mensaje"}
            </Button>
          </div>
          <div className="bg-[#111] border border-neutral-800 rounded-md p-6">
            <label className="text-xs uppercase font-bold text-neutral-400 block mb-3">Vista previa</label>
            <div className="bg-[#0b141a] rounded-md p-4 min-h-[120px]">
              <div className="bg-[#005c4b] text-white text-sm rounded-lg rounded-tr-none p-3 max-w-[85%] ml-auto whitespace-pre-wrap" data-testid="wa-preview">
                {preview() || <span className="text-white/40">El mensaje aparece acá...</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "plantillas" && (
        <div>
          <Button onClick={() => { setTplEdit({ name: "", body: "" }); setTplId(null); }} className="bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase text-xs mb-4" data-testid="new-template-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva plantilla
          </Button>
          <div className="grid md:grid-cols-2 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="bg-[#171717] border border-neutral-800 rounded-md p-5" data-testid={`template-card-${t.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-heading font-bold text-sm uppercase">{t.name}</h3>
                  <div className="flex gap-2">
                    <button onClick={() => { setTplEdit({ name: t.name, body: t.body }); setTplId(t.id); }} className="text-neutral-500 hover:text-white" data-testid={`template-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deleteTemplate(t.id)} className="text-neutral-500 hover:text-red-400" data-testid={`template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-sm text-neutral-400 whitespace-pre-wrap leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-4">Tip: usá variables como {"{{nombre}}"}, {"{{numero}}"} y {"{{total}}"} que se completan al enviar.</p>
        </div>
      )}

      {tab === "historial" && (
        <div className="bg-[#171717] border border-neutral-800 rounded-md overflow-hidden">
          {logs.length === 0 && <p className="text-neutral-500 text-sm p-5">Sin mensajes enviados todavía.</p>}
          {logs.map((l) => (
            <div key={l.id} className="px-5 py-3 border-b border-neutral-800 last:border-0 flex gap-4 items-start" data-testid={`wa-log-${l.id}`}>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-sm border shrink-0 mt-0.5 ${l.mock ? "text-[#F2B90D] border-[#F2B90D]/40 bg-[#F2B90D]/10" : "text-green-400 border-green-500/40 bg-green-500/10"}`}>
                {l.mock ? "Simulado" : "Enviado"}
              </span>
              <div className="min-w-0">
                <p className="text-xs text-neutral-400 font-mono">{l.phone} · {new Date(l.created_at).toLocaleString("es-AR")}</p>
                <p className="text-sm text-neutral-300 mt-1 whitespace-pre-wrap">{l.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "migracion" && (
        <div className="space-y-6">
          <WhatsAppMigrationWidget />
          <div>
            <h2 className="font-heading font-bold uppercase text-sm tracking-wider text-neutral-400 mb-3">
              Progreso de todos ({migrationInstances.length})
            </h2>
            <div className="bg-[#171717] border border-neutral-800 rounded-md overflow-hidden">
              {migrationInstances.length === 0 && (
                <p className="text-neutral-500 text-sm p-5">Todavía nadie empezó su migración.</p>
              )}
              {migrationInstances.map((i) => (
                <div key={i.id} className="px-5 py-3 border-b border-neutral-800 last:border-0 flex items-center justify-between gap-4 flex-wrap" data-testid={`wa-mig-inst-${i.id}`}>
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-sm truncate">{i.label}</p>
                    <p className="text-xs text-neutral-500">{MIG_STATUS_LABEL[i.status] || i.status}{i.phone ? ` · ${i.phone}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-neutral-400 shrink-0">
                    <span>{i.stats.chats_enabled} charla(s)</span>
                    <span>{i.stats.clients_detected} cliente(s)</span>
                    <span>{i.stats.providers_detected} proveedor(es)</span>
                    <span>{i.stats.jobs_detected} trabajo(s)</span>
                    <Button size="sm" variant="outline" onClick={() => deleteMigrationInstance(i)}
                      className="border-red-900 rounded-sm h-7 px-2 text-red-400 hover:bg-red-500/10 hover:text-red-400"
                      data-testid={`wa-mig-inst-delete-${i.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">Configurar Evolution API</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-neutral-400">Cuando tengas tu instancia de Evolution API, completá estos datos para activar los envíos reales de WhatsApp.</p>
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">URL del servidor</label>
              <Input placeholder="https://mi-evolution-api.com" value={settingsForm.api_url} onChange={(e) => setSettingsForm({ ...settingsForm, api_url: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-settings-url" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">API Key</label>
              <Input type="password" placeholder={settings?.api_key_masked ? `${settings.api_key_masked} — dejar vacío para no cambiarla` : "AUTHENTICATION_API_KEY"}
                value={settingsForm.api_key} onChange={(e) => setSettingsForm({ ...settingsForm, api_key: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-settings-key" />
              {settings?.api_key_masked && <p className="text-[11px] text-neutral-500 mt-1">Clave actual: {settings.api_key_masked}</p>}
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Nombre de instancia</label>
              <Input placeholder="mi-instancia" value={settingsForm.instance} onChange={(e) => setSettingsForm({ ...settingsForm, instance: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-settings-instance" />
            </div>
            <Button onClick={saveSettings} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="wa-settings-save">Guardar configuración</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">Vincular WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            <p className="text-xs text-neutral-400">
              Abrí WhatsApp en el celular del negocio → Dispositivos vinculados → Vincular un dispositivo, y escaneá este código.
            </p>
            <div className="bg-white rounded-md p-4 flex items-center justify-center min-h-[280px]">
              {loadingQr && <p className="text-neutral-500 text-sm">Generando código...</p>}
              {!loadingQr && qr && <img src={qr} alt="Código QR de WhatsApp" className="w-64 h-64" data-testid="wa-qr-image" />}
            </div>
            <p className="text-[11px] text-neutral-500">El código expira rápido — si se vence, pedí uno nuevo.</p>
            <Button variant="outline" onClick={fetchQr} disabled={loadingQr} className="w-full border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="wa-qr-refresh">
              Pedir código nuevo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkChat} onOpenChange={(o) => !o && setLinkChat(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">Vincular charla a un cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Cliente existente</label>
              <select value={linkClientId} onChange={(e) => setLinkClientId(e.target.value)}
                className="w-full h-10 bg-neutral-900 border border-neutral-800 rounded-sm px-3 text-sm" data-testid="wa-link-client-select">
                <option value="">— Ninguno —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.phone})</option>)}
              </select>
            </div>
            <p className="text-xs text-neutral-500 text-center">— o —</p>
            <div>
              <label className="text-xs uppercase font-bold text-neutral-400 block mb-1">Crear cliente nuevo</label>
              <Input placeholder="Nombre" value={linkName} onChange={(e) => { setLinkName(e.target.value); setLinkClientId(""); }}
                className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="wa-link-name-input" />
            </div>
            <Button onClick={confirmLink} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="wa-link-confirm-btn">
              Vincular
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fullImage} onOpenChange={(o) => !o && setFullImage(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading uppercase">Imagen</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center min-h-[200px]">
            {fullImage?.loading && <p className="text-neutral-500 text-sm">Descargando imagen...</p>}
            {fullImage?.src && <img src={fullImage.src} alt="" className="max-w-full max-h-[70vh] rounded-md" />}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tplEdit} onOpenChange={(o) => !o && setTplEdit(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{tplId ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>
          {tplEdit && (
            <div className="space-y-3">
              <Input placeholder="Nombre de la plantilla" value={tplEdit.name} onChange={(e) => setTplEdit({ ...tplEdit, name: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="template-name-input" />
              <Textarea placeholder="Mensaje... Podés usar {{nombre}}, {{numero}}, {{total}}" rows={5} value={tplEdit.body} onChange={(e) => setTplEdit({ ...tplEdit, body: e.target.value })} className="bg-neutral-900 border-neutral-800 rounded-sm" data-testid="template-body-input" />
              <Button onClick={saveTemplate} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="template-save-btn">Guardar plantilla</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
